import { consume } from '@lit/context';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { LitElement, PropertyValueMap, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import {
  ConfigurationDto,
  postSaveConfiguration,
  postSaveDraftConfiguration,
} from '@/api-sdk';
import { FgDialog } from '@/components';
import { iconSave } from '@/components/icons';
import {
  ConfiguratorContextType,
  GlobalComponentType,
  PersonalisationType,
  PrintExampleContextType,
  configuratorGlobal,
  global,
  personalisation,
  printExample,
} from '@/context';
import { baseStyles, buttonStyles, formStyles } from '@/styles';
import { MessageTypes } from '@/types';

import '../../loader/fg-loader';

@customElement('fg-draft-save')
export class FgDraftSave extends LitElement {
  @property({ type: Object })
  configuration?: ConfigurationDto | null;

  @property({ type: String })
  shopUrl?: string;

  @state()
  configurationId?: string;

  @state()
  draftName = '';

  @state()
  saved = false;

  @state()
  invalidName = false;

  @state()
  invalidTimeout?: ReturnType<typeof setTimeout>;

  @state()
  message?: {
    message: string;
    type: MessageTypes;
  };

  @state()
  isLoading = false;

  @state()
  link?: string;

  @state()
  postSaveDraftConfigurationPromise?: Promise<any>;

  @state()
  postSaveConfigurationPromise?: Promise<string>;

  @state()
  postSaveDraftConfigurationController: AbortController | null = null;

  @state()
  postSaveConfigurationController: AbortController | null = null;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context })
  configuratorContext!: ConfiguratorContextType;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext: PersonalisationType[] = [];

  @consume({ context: printExample.context, subscribe: true })
  printExampleContext?: PrintExampleContextType;

  private fgDialogRef: Ref<FgDialog> = createRef();
  private draftNameInput: Ref<HTMLInputElement> = createRef();

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    const { shopUrl } = this.configuratorContext;

    if (_changedProperties.has('configuration')) {
      this.message = undefined;
      this.handleValidationMessage();
    }

    if (shopUrl) {
      this.link = `${shopUrl}fg_draftconfigurations/list/`;
    }
  }

  async saveConfiguration() {
    if (
      this.postSaveConfigurationPromise !== undefined &&
      this.postSaveConfigurationController
    ) {
      this.postSaveConfigurationController.abort();
    }

    if (!this.configuration) {
      return;
    }

    const { debtor, country, locale } = this.globalContext;
    this.postSaveConfigurationController = new AbortController();
    this.postSaveConfigurationPromise = postSaveConfiguration(
      {
        params: {
          country,
          debtor,
        },
        data: this.configuration,
      },
      {
        signal: this.postSaveConfigurationController.signal,
      },
    );

    try {
      this.configurationId = await this.postSaveConfigurationPromise;
    } catch (error) {
      if (isAxiosError(error) && error?.name === 'CanceledError') {
        return;
      }
      console.error('Could not save the configuration', error);
      this.isLoading = false;
    } finally {
      this.postSaveConfigurationPromise = undefined;
      if (this.configurationId) {
        try {
          await this.handlePersonalisationAndPrintExample(
            this.configurationId,
            locale,
          );
        } catch (error) {
          console.error(
            'We could not save the personalisation or de print example',
            error,
          );
        }

        this.saveDraftConfiguration();
      }
    }
  }

  async saveDraftConfiguration() {
    const { customerId, debtor, vatRate } = this.globalContext;
    if (
      this.postSaveDraftConfigurationPromise !== undefined &&
      this.postSaveDraftConfigurationController
    ) {
      this.postSaveDraftConfigurationController.abort();
    }

    if (!debtor || !this.configurationId || !customerId) {
      return;
    }
    this.postSaveDraftConfigurationController = new AbortController();
    this.postSaveDraftConfigurationPromise = postSaveDraftConfiguration(
      {
        debtorCode: debtor,
        data: {
          configurationId: this.configurationId,
          customerId: customerId,
          name: this.draftName,
          vatRate: vatRate || '0.2100',
        },
      },
      {
        signal: this.postSaveDraftConfigurationController.signal,
      },
    );

    try {
      await this.postSaveDraftConfigurationPromise;
      this.saved = true;
    } catch (error) {
      if (isAxiosError(error) && error?.name === 'CanceledError') {
        return;
      }

      this.saved = false;
      console.error('Could not save the configuration as draft', error);
    } finally {
      this.postSaveDraftConfigurationPromise = undefined;
      this.isLoading = false;
    }
  }

  async handlePersonalisationAndPrintExample(
    configurationId: string,
    locale: string,
  ) {
    const uploadPromises = [];

    if (this.personalisationContext && this.personalisationContext.length > 0) {
      uploadPromises.push(
        personalisation.save(
          this.personalisationContext,
          configurationId,
          locale,
        ),
      );
    }
    if (this.printExampleContext) {
      uploadPromises.push(
        printExample.save(this.printExampleContext?.service, configurationId),
      );
    }

    return Promise.allSettled(uploadPromises);
  }

  handleSaveDraftConfiguration(event: FormDataEvent) {
    event.preventDefault();

    if (!this.draftNameInput.value?.value) {
      this.draftNameInput.value?.focus();
      if (this.invalidTimeout) {
        clearTimeout(this.invalidTimeout);
      }
      this.invalidTimeout = setTimeout(() => {
        this.invalidName = false;
      }, 2000);
      this.invalidName = true;
      return;
    }

    this.draftName = this.draftNameInput.value?.value;
    this.isLoading = true;
    this.saveConfiguration();
  }

  handleCloseDraftModal() {
    this.fgDialogRef.value?.close();
    this.saved = false;
  }

  saveDraftConfigurationPopupContent() {
    switch (this.saved) {
      case false:
        return html`
          <form id="draft-form" @submit=${this.handleSaveDraftConfiguration}>
            <label for="draft-name">${t('labels.name')}</label>
            <input
              class=${classMap({ warning: this.invalidName })}
              id="draft-name"
              name="draft-name"
              aria-invalid=${this.invalidName}
              ${ref(this.draftNameInput)}
              placeholder="${ifDefined(
                t('content.drafts.draftNamePlaceholder') || undefined,
              )}"
            />
            <div class="button-group">
              <button
                class="button button-tertiary"
                @click=${this.handleCloseDraftModal}
              >
                ${t('actions.close')}
              </button>
              <button type="submit" class="button button-primary">
                ${t('actions.save')}
              </button>
            </div>
          </form>
        `;
      case true:
        return html`
          <div>
            ${unsafeHTML(
              t('content.drafts.draftSaved', { link: this.link || '#' }),
            )}
            <div slot="footer" class="button-group">
              <button
                class="button button-tertiary"
                @click=${this.handleCloseDraftModal}
              >
                ${t('actions.close')}
              </button>
              <a href=${this.link || '#'} class="button button-primary">
                ${t('actions.viewDrafts')}
              </a>
            </div>
          </div>
        `;
    }
  }

  openDraftModal() {
    if (this.configuration) {
      this.fgDialogRef.value?.open();
      return;
    }
    this.message = {
      message: t('content.drafts.chooseVariant'),
      type: MessageTypes.ERROR,
    };
    this.handleValidationMessage();
  }

  handleValidationMessage() {
    this.dispatchEvent(
      new CustomEvent('show-draft-validation-message', {
        detail: this.message,
        composed: true,
      }),
    );
  }

  closeDraftModal() {
    this.saved = false;
    if (this.postSaveConfigurationPromise) {
      this.postSaveConfigurationController?.abort();
    }

    if (this.postSaveDraftConfigurationPromise) {
      this.postSaveDraftConfigurationController?.abort();
    }
  }

  render() {
    if (!this.globalContext.debtor) {
      return nothing;
    }

    return html`
      <button
        @click=${() => this.openDraftModal()}
        class="save-draft button button-link"
      >
        ${iconSave} ${t('actions.saveConfiguration')}
      </button>

      <fg-dialog
        ${ref(this.fgDialogRef)}
        part="wholesale"
        compact
        closable
        @dialog-closed=${this.closeDraftModal}
      >
        <h5 slot="title">${t('content.drafts.saveDraftTitle')}</h5>

        ${this.isLoading
          ? html`
              <fg-loader type="in-component"></fg-loader>
            `
          : nothing}
        ${this.saveDraftConfigurationPopupContent()}
      </fg-dialog>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    formStyles,
    css`
      button.save-draft {
        text-decoration: none;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        margin: 0 auto;
        &&:hover {
          box-shadow: none;
        }
      }

      form {
        display: flex;
        width: 100%;
        flex-direction: column;
      }

      .button-group {
        margin-top: 24px;
        width: 100%;
        display: flex;
        gap: 16px;

        & .button {
          width: 100%;
        }

        & a {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    `,
  ];
}
